import { SupportedUserRoles, UserAccountInfo } from '@insurancemenu/imenu-components-lib/dist/types';
import apolloClient from 'api/apolloClient';
import { GET_USER_INFO_FOR_HEADER } from 'api/queries/userService';
import { makeAutoObservable } from 'mobx';
import { AgencyType, HeaderUserInfo } from 'types/userService';

const INITIAL_USER_INFO = {
  firstName: '',
  lastName: '',
  affiliation: '',
  sponsorName: '',
  sponsorId: '',
};

/**
 * Global store. User data is retrieved on entry in BrokerApp or EmployeeApp.
 * It may be updated with re-login in the EmployeeApp using getUserData().
 */

export class UserStore {
  userFullName = '';
  userRoles: SupportedUserRoles[] = [];
  userInfo: UserAccountInfo = INITIAL_USER_INFO;
  sponsorId = '';
  imAgencyId = '';
  private _agencyId = '';
  private _agencyType: AgencyType | null = null;
  private _isLoading = false;

  public get coreUserInfo(): HeaderUserInfo {
    return {
      firstName: this.userFullName,
      lastName: this.userInfo.lastName,
      affiliation: this.userInfo.affiliation,
      sponsorName: this.userInfo.sponsorName,
      sponsorId: this.sponsorId,
      userRoles: this.userRoles,
      imAgencyId: this.imAgencyId,
      agencyId: this._agencyId,
      agencyType: this._agencyType!,
    };
  }

  public updateUserInfo(userInfo: HeaderUserInfo): void {
    const { firstName, lastName, affiliation, sponsorName, sponsorId, userRoles, imAgencyId, agencyId, agencyType } =
      userInfo;

    this.userFullName = `${firstName} ${lastName}`;
    this.userInfo = { firstName, lastName, affiliation, sponsorName, sponsorId };
    this.userRoles = userRoles;
    this.sponsorId = sponsorId;
    this.imAgencyId = imAgencyId;
    this._agencyId = agencyId;
    this._agencyType = agencyType;
  }

  public setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public async getUserData(): Promise<void> {
    this.setIsLoading(true);
    const res = await apolloClient.query<{ getUserInfoForHeader: HeaderUserInfo }>({
      query: GET_USER_INFO_FOR_HEADER,
      fetchPolicy: 'no-cache',
    });
    this.updateUserInfo(res.data.getUserInfoForHeader);
    this.setIsLoading(false);
  }

  /** Update global user info without changing the loading state. Avoids re-rendering the whole app. */
  public async updateUserInfoQuietly(): Promise<void> {
    const res = await apolloClient.query<{ getUserInfoForHeader: HeaderUserInfo }>({
      query: GET_USER_INFO_FOR_HEADER,
      fetchPolicy: 'no-cache',
    });
    this.updateUserInfo(res.data.getUserInfoForHeader);
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
